export type RangeDays = 7 | 30 | 90;

export interface DashboardFilters {
  range: RangeDays;
  humansOnly: boolean;
  country: string | null;
}

export const RANGE_OPTIONS: { value: RangeDays; label: string }[] = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

/** Read filters from the URL so a filtered view can be shared or reloaded. */
export const parseFilters = (params: URLSearchParams): DashboardFilters => {
  const r = Number(params.get('range'));
  const range: RangeDays = r === 7 || r === 90 ? r : 30;
  const humansOnly = params.get('humans') !== '0';
  const c = (params.get('country') || '').toUpperCase();
  return { range, humansOnly, country: /^[A-Z]{2}$/.test(c) ? c : null };
};

export const writeFilters = (params: URLSearchParams, f: DashboardFilters): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.set('range', String(f.range));
  if (f.humansOnly) next.delete('humans');
  else next.set('humans', '0');
  if (f.country) next.set('country', f.country);
  else next.delete('country');
  return next;
};

export const rangeToDates = (range: RangeDays, now: Date = new Date()) => {
  const to = new Date(now);
  const from = new Date(now.getTime() - range * 86_400_000);
  return { from: from.toISOString(), to: to.toISOString() };
};

/** Percentage change vs the previous period; null when there is no baseline. */
export const percentChange = (current: number, previous: number): number | null => {
  if (!previous) return null;
  return Math.round(((current - previous) / previous) * 100);
};

export const formatChange = (current: number, previous: number): string => {
  const pct = percentChange(current, previous);
  if (pct === null) return current > 0 ? 'new this period' : 'no change';
  if (pct === 0) return 'same as previous period';
  return `${pct > 0 ? '+' : ''}${pct}% vs previous period`;
};

export interface Spike {
  date: string;
  metric: string;
  value: number;
  typical: number;
}

/**
 * Flags days that are far above the typical level of the other days.
 * Needs at least 5 days of history and a minimum absolute value to avoid noise.
 */
export const detectSpikes = <T extends Record<string, unknown>>(
  daily: T[],
  metric: keyof T & string,
  label: string,
  minValue = 10,
): Spike[] => {
  const values = daily.map((d) => Number(d[metric]) || 0);
  if (values.length < 5) return [];
  const spikes: Spike[] = [];
  values.forEach((v, i) => {
    const others = values.filter((_, j) => j !== i).sort((a, b) => a - b);
    const median = others[Math.floor(others.length / 2)];
    const typical = Math.max(median, 1);
    if (v >= minValue && v >= typical * 3) {
      spikes.push({ date: String(daily[i].d ?? daily[i].date ?? i), metric: label, value: v, typical: median });
    }
  });
  return spikes;
};

export const conversion = (part: number, whole: number): string =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : '-';
